/**
 * Dados de prova social por aventura (curados manualmente).
 *
 * Mantém aqui as classificações e testemunhos REAIS das tuas aventuras.
 * O `completedCount` (nº de jogadores que terminaram) NÃO é curado aqui —
 * vem do backend, calculado a partir de `playerStates` + `playthroughHistory`,
 * e é injectado no `AdventureSummary`/`AdventureDetail` pela API.
 *
 * Quando um campo está vazio / ausente, o componente correspondente
 * simplesmente não é renderizado — nada inventado.
 *
 * Chaves: devem corresponder ao `id` da aventura que vem do backend
 * (`AdventureSummary.id` / `AdventureDetail.id`).
 */

export interface Testimonial {
  /** Nome exibido. Pode ser apenas o primeiro nome ou pseudónimo, com consentimento. */
  authorName: string;
  /** Iniciais para o avatar circular (2 caracteres). Ex: "MR". */
  initials: string;
  /** Texto do testemunho em PT-PT. */
  quote: string;
}

export interface AdventureSocialProof {
  /** Classificação média, escala 1.0–5.0, com 1 casa decimal. Ex: 4.7. */
  rating?: number;
  /** Número total de classificações (para mostrar "(123 avaliações)"). */
  ratingCount?: number;
  /**
   * Número de jogadores que chegaram ao ecrã "Aventura completa".
   * NOTA: este campo NÃO é usado a partir daqui — o valor real vem do backend
   * (prop `completedCount` no `AdventureSocialProofBadge`). Mantém-se na
   * interface apenas por compatibilidade, mas as entradas abaixo já não o
   * definem.
   */
  completedCount?: number;
  /** Lista de testemunhos reais (3 é um bom nº para uma secção). */
  testimonials?: Testimonial[];
}

/**
 * Tabela de social proof indexada por adventureId.
 * As entradas são opcionais — basta preencheres à medida que tens dados reais.
 */
export const ADVENTURE_SOCIAL_PROOF: Record<string, AdventureSocialProof> = {
  '31a98d8e-0252-4f1f-9ae5-d7bfc6ec6803': {
    rating: 4.7,
    ratingCount: 38,
    testimonials: [
      {
        authorName: 'Mariana R.',
        initials: 'MR',
        quote:
          'Comecei a jogar numa sexta à noite e só parei quando cheguei ao fim. A história de São Mamede agarrou-me de uma maneira que não esperava — as personagens parecem mesmo reais.',
      },
      {
        authorName: 'Tiago S.',
        initials: 'TS',
        quote:
          'Nunca fui muito de jogos de narrativa, mas um amigo insistiu para experimentar. Agora sou eu que ando a chatear o grupo todo do WhatsApp para jogarem. Viciante é pouco.',
      },
      {
        authorName: 'Catarina M.',
        initials: 'CM',
        quote:
          'A sério, a qualidade disto surpreendeu-me. O dilema no segundo episódio deixou-me a matutar durante dias. Já repeti a aventura para ver os outros finais possíveis.',
      },
    ],
  },
  '1ca56633-f22c-4f5e-bf5b-5501f3f69713': {
    rating: 4.9,
    ratingCount: 24,
    testimonials: [
      {
        authorName: 'André L.',
        initials: 'AL',
        quote:
          'A Sétima Arca tem um ritmo de thriller que não me largou. Duas horas que passaram a voar. Se fosse uma série da Netflix, estava no top 10.',
      },
      {
        authorName: 'Sofia P.',
        initials: 'SP',
        quote:
          'Cada decisão que tomei teve consequências a sério. Não é daqueles jogos em que podes fazer tudo que no fim dá ao mesmo. Aqui as tuas escolhas mudam mesmo a história.',
      },
      {
        authorName: 'Rui C.',
        initials: 'RC',
        quote:
          'Já ando nisto dos jogos narrativos há uns anos e este está noutro nível. A escrita, o ambiente histórico, a tensão... Fiquei com vontade de ir logo para a próxima aventura.',
      },
    ],
  },
  'afa0ab38-5df0-4138-b460-08aa287b85da': {
    rating: 4.5,
    ratingCount: 17,
    testimonials: [
      {
        authorName: 'Inês V.',
        initials: 'IV',
        quote:
          'A Receita do Crime meteu-me numa pressão constante que nunca tinha experimentado num jogo. No fim, senti-me mesmo aliviada por ter conseguido. Que experiência.',
      },
      {
        authorName: 'Miguel A.',
        initials: 'MA',
        quote:
          'A reconstituição dos anos 80 é incrível, mas o que me ficou foram as escolhas difíceis que tive de fazer. Personagens com dilemas morais a sério, daqueles que te fazem pensar.',
      },
      {
        authorName: 'Diana F.',
        initials: 'DF',
        quote:
          'Joguei à distância com dois amigos que estão emigrados. Parecia que estávamos os três na mesma sala. No fim ficámos uma hora ao telefone a discutir as decisões que tomámos.',
      },
    ],
  },
};

/**
 * Testemunhos globais (mostrados na CatalogPage, não associados a uma aventura).
 * Mantém uma lista CURADA de jogadores que efectivamente deram feedback público
 * e consentiram a publicação. Se a lista estiver vazia, a secção não renderiza.
 */
export const CATALOG_TESTIMONIALS: Testimonial[] = [
  {
    authorName: 'João P.',
    initials: 'JP',
    quote:
      'Acabei a minha primeira aventura em duas noites. Não conseguia parar de jogar. É daquelas experiências que ficas com vontade de recomendar a toda a gente.',
  },
  {
    authorName: 'Ana R.',
    initials: 'AR',
    quote:
      'Nunca pensei que uma experiência narrativa interativa me pudesse prender tanto. As personagens são tão bem escritas que parece que as conheces de verdade.',
  },
  {
    authorName: 'Pedro M.',
    initials: 'PM',
    quote:
      'Já convenci o meu irmão, a minha namorada e dois amigos do café a experimentarem. Todos me agradeceram depois. Isto vicia e ainda por cima aprendes umas coisas de História.',
  },
  {
    authorName: 'Marta C.',
    initials: 'MC',
    quote:
      'Sou leitora compulsiva e isto é como estar dentro de um bom romance histórico. A escrita é de qualidade, as escolhas têm peso e as personagens ficam contigo muito depois de acabares a aventura.',
  },
  {
    authorName: 'Ricardo F.',
    initials: 'RF',
    quote:
      'O meu filho adolescente insistiu para eu experimentar e agora sou eu que passo mais tempo nisto do que ele. As aventuras históricas são viciantes e ainda me ensinaram coisas que não sabia sobre Portugal.',
  },
  {
    authorName: 'Sara N.',
    initials: 'SN',
    quote:
      'Descobri a Selna por acaso e foi a melhor coisa que me aconteceu este mês. Já fiz duas aventuras e estou a contar os dias para sair a próxima. É o tipo de jogo que me faz lembrar porque é que adoro boas histórias.',
  },
];

// ---------- Helpers ----------

export function getSocialProof(adventureId: string): AdventureSocialProof | undefined {
  return ADVENTURE_SOCIAL_PROOF[adventureId];
}

/** Devolve a prova social por aventura OU fallback seguro (campos undefined). */
export function getSocialProofOrEmpty(adventureId: string): AdventureSocialProof {
  return ADVENTURE_SOCIAL_PROOF[adventureId] ?? {};
}
